import { useState } from "react";
import { Trans } from "react-i18next";
import { CoreButton, CoreModal, CoreModalDescription, coreToast } from "@verity/components/core";
import i18n from "../../../../../i18n";

type DeleteAttemptModalProps = {
	/** 1-indexed attempt number, as shown in the attempt selector. */
	attemptNumber: number;
	studentName: string;
	onClose: () => void;
	onDelete: () => Promise<void>;
};

/**
 * Confirms permanently removing one test-taker attempt. Distinct from `DeleteConfirmationModal`, which
 * only deletes an attempt's media and leaves the attempt itself in place.
 */
export default function DeleteAttemptModal({ attemptNumber, studentName, onClose, onDelete }: DeleteAttemptModalProps) {
	const [deleting, setDeleting] = useState(false);

	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18n.t("result.components.resultsPage.deleteAttemptModal.title")}
			buttons={
				<>
					<CoreButton color="secondary" onClick={onClose} disabled={deleting}>
						{i18n.t("result.components.resultsPage.deleteAttemptModal.cancel")}
					</CoreButton>
					<CoreButton
						onClick={async () => {
							setDeleting(true);
							try {
								await onDelete();
								coreToast.success(i18n.t("result.components.resultsPage.deleteAttemptModal.success"));
							} catch (e) {
								console.error(e);
								coreToast.error(i18n.t("result.components.resultsPage.deleteAttemptModal.error"));
							} finally {
								setDeleting(false);
								onClose();
							}
						}}
						loading={deleting}
					>
						{i18n.t("result.components.resultsPage.deleteAttemptModal.delete")}
					</CoreButton>
				</>
			}
			maxWidth="604px"
		>
			<CoreModalDescription>
				{/* The attempt and the test-taker are bolded and the two sentences sit on their own lines, so the
				    description goes through Trans rather than i18n.t. "Attempt" and the line break both stay
				    inside the translated string, so a translation can reorder or reword around them. */}
				<Trans
					i18nKey="result.components.resultsPage.deleteAttemptModal.description"
					values={{ attempt: attemptNumber, studentName }}
					components={{ b: <strong />, br: <br /> }}
				/>
			</CoreModalDescription>
		</CoreModal>
	);
}
